import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Edit, Download, Receipt, Trash2, Mail, FileWarning, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import DocumentPreview from "../components/DocumentPreview";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

export default function DocumentView({ type }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [logoBlob, setLogoBlob] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({ to: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [rectifyOpen, setRectifyOpen] = useState(false);
  const [rectifyReason, setRectifyReason] = useState("");
  const [rectifying, setRectifying] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertMode, setConvertMode] = useState("normal");
  const [convertProject, setConvertProject] = useState("");
  const [convertContract, setConvertContract] = useState(0);
  const [converting, setConverting] = useState(false);
  const [estInvoices, setEstInvoices] = useState([]);

  const endpoint = type === "invoice" ? "invoices" : "estimates";
  const reload = () => api.get(`/${endpoint}/${id}`).then(r => setDoc(r.data));

  useEffect(() => { reload(); api.get("/email/status").then(r => setEmailEnabled(r.data.enabled)); /* eslint-disable-next-line */ }, [id, endpoint]);
  useEffect(() => {
    if (type === "estimate") {
      api.get(`/estimates/${id}/invoices`).then(r => setEstInvoices(r.data || [])).catch(() => setEstInvoices([]));
    }
  }, [id, type, doc?.converted_invoice_id]);
  useEffect(() => {
    if (user?.company?.logo_path) {
      api.get(`/files/${user.company.logo_path}`, { responseType: "blob" })
        .then(r => setLogoBlob(URL.createObjectURL(r.data)))
        .catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (doc) setEmailData(d => ({ ...d, to: doc.client_snapshot?.email || "" }));
  }, [doc]);

  const openConvert = () => {
    setConvertMode("normal");
    setConvertProject(doc?.project || "");
    setConvertContract(0);
    setConvertOpen(true);
  };

  const doConvert = async () => {
    setConverting(true);
    try {
      const r = await api.post(`/estimates/${id}/convert`, {
        invoice_type: convertMode,
        project: convertProject || null,
        contract_total: parseFloat(convertContract) || 0,
      });
      toast.success(convertMode === "origin" ? "Factura a origen creada. Edita las mediciones del mes." : "Convertido a factura");
      setConvertOpen(false);
      navigate(`/app/facturas/${r.data.id}/editar`);
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error(e.response.data?.detail || "Funcionalidad Pro", {
          action: { label: "Hazte Pro", onClick: () => navigate("/app/planes") },
          duration: 8000,
        });
      } else {
        toast.error(e.response?.data?.detail || "Error al convertir");
      }
    }
    finally { setConverting(false); }
  };

  const markPaid = async () => {
    await api.post(`/invoices/${id}/mark-paid`);
    toast.success("Factura marcada como cobrada");
    reload();
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    await api.delete(`/${endpoint}/${id}`);
    toast.success("Eliminado");
    navigate(type === "invoice" ? "/app/facturas" : "/app/presupuestos");
  };

  const downloadPdf = () => {
    const token = localStorage.getItem("vel7o_token");
    const url = `${API}/${endpoint}/${id}/pdf?auth=${token}`;
    window.open(url, "_blank");
  };

  const sendEmail = async () => {
    if (!emailData.to) { toast.error("Indica un destinatario"); return; }
    setSending(true);
    try {
      await api.post(`/${endpoint}/${id}/send`, emailData);
      toast.success("Enviado correctamente");
      setEmailDialogOpen(false);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setSending(false); }
  };

  const doRectify = async () => {
    if (!rectifyReason.trim()) { toast.error("Indica el motivo de la rectificación"); return; }
    setRectifying(true);
    try {
      const r = await api.post(`/invoices/${id}/rectify`, { reason: rectifyReason });
      toast.success("Factura rectificativa creada");
      setRectifyOpen(false);
      navigate(`/app/facturas/${r.data.id}/editar`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al rectificar"); }
    finally { setRectifying(false); }
  };

  if (!doc) return <div className="text-white/50">Cargando...</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="document-view">
      <header className="flex justify-between items-center flex-wrap gap-3 no-print">
        <Button variant="ghost" onClick={() => navigate(type === "invoice" ? "/app/facturas" : "/app/presupuestos")} className="text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4 mr-2" /> Volver</Button>
        <div className="flex gap-2 flex-wrap">
          {type === "estimate" && (
            <>
              <Button data-testid="edit-doc-btn" variant="outline" onClick={() => navigate(`/app/presupuestos/${id}/editar`)} className="border-white/20 bg-transparent text-white hover:bg-white/5"><Edit className="w-4 h-4 mr-2" /> Editar</Button>
              <Button data-testid="convert-invoice-btn" onClick={openConvert} className="bg-brand-orange hover:bg-brand-orange-hover"><Receipt className="w-4 h-4 mr-2" /> Facturar</Button>
            </>
          )}
          {type === "invoice" && (
            <>
              <Button data-testid="edit-invoice-btn" variant="outline" onClick={() => navigate(`/app/facturas/${id}/editar`)} className="border-white/20 bg-transparent text-white hover:bg-white/5"><Edit className="w-4 h-4 mr-2" /> Editar</Button>
              {!doc.paid && <Button data-testid="mark-paid-btn" onClick={markPaid} className="bg-emerald-500 hover:bg-emerald-600">Marcar cobrada</Button>}
              {doc.invoice_type !== "rectificativa" && <Button data-testid="rectify-btn" variant="outline" onClick={() => setRectifyOpen(true)} className="border-amber-500/40 bg-transparent text-amber-400 hover:bg-amber-500/10"><FileWarning className="w-4 h-4 mr-2" /> Rectificar</Button>}
            </>
          )}
          <Button data-testid="email-btn" variant="outline" onClick={() => setEmailDialogOpen(true)} disabled={!emailEnabled} title={!emailEnabled ? "Email deshabilitado (RESEND_API_KEY no configurada)" : "Enviar por email"} className="border-white/20 bg-transparent text-white hover:bg-white/5 disabled:opacity-40"><Mail className="w-4 h-4 mr-2" /> Email</Button>
          <Button data-testid="pdf-btn" onClick={downloadPdf} className="bg-brand-cyan hover:bg-brand-cyan-hover text-surface-base font-semibold"><Download className="w-4 h-4 mr-2" /> PDF</Button>
          <Button variant="ghost" onClick={remove} className="text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
        </div>
      </header>

      <div className="overflow-x-auto py-4">
        <DocumentPreview doc={doc} company={user?.company} type={type} logoUrl={logoBlob} />
      </div>

      {type === "estimate" && estInvoices.length > 0 && (
        <section className="rounded-lg border border-white/10 bg-[#0A1128]/40 p-5 no-print" data-testid="est-invoices-list">
          <h2 className="font-display font-semibold text-lg mb-3 flex items-center gap-2"><Receipt className="w-5 h-5 text-brand-orange" strokeWidth={1.5} /> Facturas emitidas de este presupuesto</h2>
          <div className="space-y-2">
            {estInvoices.map((iv) => (
              <div key={iv.id} onClick={() => navigate(`/app/facturas/${iv.id}`)} className="flex justify-between items-center p-3 rounded border border-white/5 hover:border-brand-cyan/40 hover:bg-white/5 cursor-pointer transition-colors" data-testid={`est-inv-${iv.id}`}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-white/60">{iv.number}</span>
                  {iv.is_origin && <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-brand-cyan/15 text-brand-cyan">A origen</span>}
                  {iv.invoice_type === "rectificativa" && <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider bg-amber-500/15 text-amber-400">Rectif.</span>}
                  <span className="text-xs text-white/50">{(iv.issue_date || "").slice(0,10)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${iv.paid ? "bg-emerald-500/15 text-emerald-400" : "bg-brand-orange/15 text-brand-orange"}`}>{iv.paid ? "cobrado" : "pendiente"}</span>
                  <span className="font-semibold text-sm tabular-nums">{(iv.total || 0).toFixed(2)} €</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="bg-[#0A1128] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display">Facturar presupuesto {doc?.number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Tipo de factura</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setConvertMode("normal")} data-testid="convert-mode-normal"
                  className={`p-3 rounded border text-left transition-all ${convertMode === "normal" ? "border-brand-orange bg-brand-orange/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}>
                  <Receipt className="w-4 h-4 mb-1.5 text-brand-orange" strokeWidth={1.5} />
                  <div className="font-semibold text-sm">Normal</div>
                  <div className="text-[11px] text-white/50">Factura única con las cantidades del presupuesto</div>
                </button>
                <button type="button" onClick={() => setConvertMode("origin")} data-testid="convert-mode-origin"
                  className={`p-3 rounded border text-left transition-all ${convertMode === "origin" ? "border-brand-cyan bg-brand-cyan/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}>
                  <Sparkles className="w-4 h-4 mb-1.5 text-brand-cyan" strokeWidth={1.5} />
                  <div className="font-semibold text-sm">A origen</div>
                  <div className="text-[11px] text-white/50">Certificación mensual (mediciones acumulativas)</div>
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Obra / referencia</Label>
              <Input data-testid="convert-project" value={convertProject} onChange={(e) => setConvertProject(e.target.value)} placeholder="Ej: Reforma local C/ Mayor 14" className="bg-black/50 border-white/10 text-white" />
              {convertMode === "origin" && <p className="text-[11px] text-white/40">Las próximas facturas de este presupuesto heredarán automáticamente las mediciones de la anterior.</p>}
            </div>

            {convertMode === "origin" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Total contrato (€) <span className="text-white/40 normal-case font-normal">— opcional</span></Label>
                <Input data-testid="convert-contract" type="number" step="0.01" value={convertContract} onChange={(e) => setConvertContract(e.target.value)} placeholder="Importe total contratado" className="bg-black/50 border-white/10 text-white" />
              </div>
            )}

            {convertMode === "origin" && estInvoices.length > 0 && (
              <div className="text-xs p-3 rounded bg-brand-cyan/10 border border-brand-cyan/30 text-brand-cyan">
                Ya hay {estInvoices.length} factura{estInvoices.length === 1 ? "" : "s"} de este presupuesto. La nueva cogerá las mediciones acumuladas de la última.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)} className="border-white/20 bg-transparent">Cancelar</Button>
            <Button data-testid="convert-confirm" onClick={doConvert} disabled={converting} className="bg-brand-orange hover:bg-brand-orange-hover">{converting ? "Creando..." : "Crear factura"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="bg-[#0A1128] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">Enviar por email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Para</Label>
              <Input data-testid="email-to" type="email" value={emailData.to} onChange={(e) => setEmailData({ ...emailData, to: e.target.value })} className="mt-1.5 bg-black/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Asunto (opcional)</Label>
              <Input value={emailData.subject} onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })} placeholder={`${type === "invoice" ? "Factura" : "Presupuesto"} ${doc.number}`} className="mt-1.5 bg-black/50 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Mensaje (opcional)</Label>
              <Textarea value={emailData.message} onChange={(e) => setEmailData({ ...emailData, message: e.target.value })} rows={4} className="mt-1.5 bg-black/50 border-white/10 text-white" />
            </div>
            <div className="text-xs text-white/40">Se adjuntará automáticamente el PDF.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)} className="border-white/20 bg-transparent">Cancelar</Button>
            <Button data-testid="email-send" onClick={sendEmail} disabled={sending} className="bg-brand-orange hover:bg-brand-orange-hover">{sending ? "Enviando..." : "Enviar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rectifyOpen} onOpenChange={setRectifyOpen}>
        <DialogContent className="bg-[#0A1128] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">Crear factura rectificativa</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-white/60">Se creará una nueva factura rectificativa enlazada a <strong className="text-white">{doc?.number}</strong> con los importes en negativo. Después podrás editarla.</p>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-white/70">Motivo de la rectificación *</Label>
              <Textarea data-testid="rectify-reason" value={rectifyReason} onChange={(e) => setRectifyReason(e.target.value)} placeholder="Ej: Error en cantidades, anulación de servicio, cambio de precio…" rows={3} className="mt-1.5 bg-black/50 border-white/10 text-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRectifyOpen(false)} className="border-white/20 bg-transparent">Cancelar</Button>
            <Button data-testid="rectify-confirm" onClick={doRectify} disabled={rectifying} className="bg-amber-500 hover:bg-amber-600 text-surface-base font-semibold">{rectifying ? "Creando..." : "Crear rectificativa"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
